import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { catchError, combineLatest, EMPTY, map, Observable, of, switchMap, take } from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { ConnectorInfo } from '@blaze-shared/api-contracts-domain';
import {
  blazeActions,
  ProjectContextFacade,
  projectContextSelectors,
} from '@blaze-shared/app-state-data-access';
import { ConnectorsCollectionService } from '@blaze-shared/connectors-data-access';
import {
  API_BASE_URL,
  LocalStorageService,
  NotificationService,
  StorageKey,
} from '@blaze-shared/platform-data-access';

@Injectable({ providedIn: 'root' })
export class ConnectorsService {
  private readonly connectorsCollService = inject(ConnectorsCollectionService);
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly notificationService = inject(NotificationService);
  private readonly projectContextFacade = inject(ProjectContextFacade);
  private readonly store = inject<Store<any>>(Store);

  /**
   * Creates specified connector based on the provided config. If a connector is created using Connection Selector
   * component, appends additional params to the request.
   * Any connector which can be configured using Connection Selector MUST use this function to create or update
   * connectors.
   */
  createConnector(config: ConnectorInfo, projectId?: string): Observable<ConnectorInfo> {
    return combineLatest({
      projectId: projectId ? of(projectId) : this.store.select(runtimeSelectors.selectProjectId),
      externalAccountRowId: this.store.select(projectContextSelectors.selectExternalAccountRowId),
    }).pipe(
      take(1),
      map(({ externalAccountRowId, projectId }) => {
        externalAccountRowId =
          externalAccountRowId || this.projectContextFacade.varAccount?._id || '';
        const isExternalAccountConnector = this.localStorageService.get(
          StorageKey.IsExternalAccountConnector,
        );
        return !isExternalAccountConnector
          ? { ...config }
          : { ...config, externalAccountRowId, projectId };
      }),
      switchMap((connectorConfig) => {
        return this.connectorsCollService.add(connectorConfig);
      }),
      catchError((err) => {
        this.notificationService.showError(err);
        if (config.templateId) {
          this.store.dispatch(blazeActions.createConnectorError({ templateId: config.templateId }));
        }
        return EMPTY;
      }),
    );
  }

  makeWebhookUrl(baseUrl: string, webhookId: string): string {
    return `${this.apiBaseUrl}${baseUrl}/${webhookId}`;
  }
}
